import {
  CLOSE_PURPOSES,
  SIBLING_PURPOSES,
  canonicalPurpose,
  extractLogDay,
  readEventPnl,
  readOrderNotional,
} from './manualChartDays.js';

export const DEFAULT_TARGET_MARGIN_PCT = 0.6;
// Half-width of the "no alvo" band, in percentage points, around the live target.
export const TARGET_PROXIMITY_PCT = 0.2;

// Float slack so a clean boundary still lands inside the window.
const MARGIN_EPS = 1e-9;

export const EXIT_QUALITY_BANDS = [
  { id: 'below', label: 'Abaixo' },
  { id: 'onTarget', label: 'No alvo' },
  { id: 'above', label: 'Acima do alvo' },
];

export function resolveExitTargetPct(pct, fallback = DEFAULT_TARGET_MARGIN_PCT) {
  const n = Number(pct);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

export function median(values) {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 1) return sorted[mid];
  return (sorted[mid - 1] + sorted[mid]) / 2;
}

export function resolveProximityPct(pct, fallback = TARGET_PROXIMITY_PCT) {
  const n = Number(pct);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
}

export function exitQualityWindow(targetMarginPct, proximityPct = TARGET_PROXIMITY_PCT) {
  const target = resolveExitTargetPct(targetMarginPct);
  const proximity = resolveProximityPct(proximityPct);
  return {
    targetMarginPct: target,
    proximityPct: proximity,
    onTargetLowPct: target - proximity,
    onTargetHighPct: target + proximity,
  };
}

export function bandIdForMargin(marginPct, targetMarginPct, proximityPct = TARGET_PROXIMITY_PCT) {
  const { onTargetLowPct, onTargetHighPct } = exitQualityWindow(targetMarginPct, proximityPct);
  if (marginPct > onTargetHighPct + MARGIN_EPS) return 'above';
  if (marginPct >= onTargetLowPct - MARGIN_EPS) return 'onTarget';
  return 'below';
}

function emptyLeg() {
  return { count: 0, notional: 0, pnl: 0 };
}

function emptyBand(def) {
  return {
    id: def.id,
    label: def.label,
    count: 0,
    notional: 0,
    pnl: 0,
    closeLong: emptyLeg(),
    closeShort: emptyLeg(),
  };
}

function summarizeLeg(events, purpose) {
  const subset = events.filter((event) => event.purpose === purpose);
  const notional = subset.reduce((sum, event) => sum + event.notional, 0);
  const pnl = subset.reduce((sum, event) => sum + event.pnl, 0);
  return {
    count: subset.length,
    notional,
    pnl,
    weightedPct: notional > 0 ? (pnl / notional) * 100 : null,
  };
}

function summarizeDay(bucket) {
  const { events } = bucket;
  const notional = events.reduce((sum, event) => sum + event.notional, 0);
  const pnl = events.reduce((sum, event) => sum + event.pnl, 0);
  return {
    dayKey: bucket.dayKey,
    label: bucket.label,
    weightedPct: notional > 0 ? (pnl / notional) * 100 : null,
    medianPct: median(events.map((event) => event.marginPct)),
    notional,
    pnl,
    count: events.length,
    closeLong: summarizeLeg(events, 'closeLong'),
    closeShort: summarizeLeg(events, 'closeShort'),
  };
}

/**
 * Exit quality for the loaded period.
 * Margin of an event is metrics.pnl / exit notional (the same USD the close chart plots).
 * Days follow the close chart axis: any sibling purpose opens the UTC day, even with no close.
 */
export function computeExitQuality(logs, options = {}) {
  const bandWindow = exitQualityWindow(options.targetMarginPct, options.proximityPct);
  const { targetMarginPct, proximityPct, onTargetLowPct, onTargetHighPct } = bandWindow;
  const dayMap = new Map();
  const histogram = EXIT_QUALITY_BANDS.map((def) => emptyBand(def));
  const bandById = new Map(histogram.map((band) => [band.id, band]));

  for (const log of logs || []) {
    if (!log || log.processed === false) continue;
    if (!SIBLING_PURPOSES.has(log.purpose)) continue;

    const day = extractLogDay(log);
    if (!day) continue;

    let bucket = dayMap.get(day.dayKey);
    if (!bucket) {
      bucket = { dayKey: day.dayKey, label: day.label, events: [] };
      dayMap.set(day.dayKey, bucket);
    }

    if (!CLOSE_PURPOSES.has(log.purpose)) continue;

    const notional = readOrderNotional(log);
    if (!(notional > 0)) continue;

    const rawPnl = readEventPnl(log);
    const pnl = Number.isFinite(rawPnl) ? rawPnl : 0;
    const marginPct = (pnl / notional) * 100;
    const purpose = canonicalPurpose(log.purpose);
    const event = { purpose, notional, pnl, marginPct };
    bucket.events.push(event);

    const band = bandById.get(bandIdForMargin(marginPct, targetMarginPct, proximityPct));
    band.count += 1;
    band.notional += notional;
    band.pnl += pnl;
    const leg = purpose === 'closeShort' ? band.closeShort : band.closeLong;
    leg.count += 1;
    leg.notional += notional;
    leg.pnl += pnl;
  }

  const days = Array.from(dayMap.values())
    .sort((a, b) => a.dayKey.localeCompare(b.dayKey))
    .map(summarizeDay);

  return {
    targetMarginPct,
    proximityPct,
    onTargetLowPct,
    onTargetHighPct,
    days,
    histogram,
    eventCount: histogram.reduce((sum, band) => sum + band.count, 0),
  };
}
